import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ProductShape } from '../../types/nodeConfig';
import {
  DEFAULT_PRODUCT_COLOR,
  DEFAULT_PRODUCT_SHAPE,
  DEFAULT_PRODUCT_SIZE,
  MAX_PRODUCT_SIZE,
  MIN_PRODUCT_SIZE,
  normalizeShape,
  previewProductSize,
  PRODUCT_SHAPES,
  productShapeSvgPath,
  resolveProductVisual,
} from '../../lib/productVisualGeometry';

interface VisualValues {
  color?: string;
  shape?: ProductShape | string;
  size?: number;
}

interface Props extends VisualValues {
  readOnly?: boolean;
  onChange: (patch: Required<Pick<VisualValues, 'color' | 'shape' | 'size'>>) => void;
}

function resolved(values: VisualValues) {
  const visual = resolveProductVisual(values);
  return {
    color: visual.color,
    shape: visual.shape,
    size: visual.size_px,
  };
}

function ShapeSvg({
  shape,
  color,
  size,
  className,
}: {
  shape: string;
  color: string;
  size: number;
  className?: string;
}) {
  const dim = Math.max(8, size);
  const stroke = '#1a1a1a';
  const spec = productShapeSvgPath(shape, dim);

  let body: JSX.Element;
  if (spec.element === 'rect') {
    const { x, y, width, height, strokeWidth } = spec.attrs;
    body = (
      <rect
        x={x as number}
        y={y as number}
        width={width as number}
        height={height as number}
        fill={color}
        stroke={stroke}
        strokeWidth={strokeWidth as number}
      />
    );
  } else if (spec.element === 'polygon') {
    body = (
      <polygon
        points={spec.attrs.points as string}
        fill={color}
        stroke={stroke}
        strokeWidth={spec.attrs.strokeWidth as number}
        strokeLinejoin="round"
      />
    );
  } else {
    body = (
      <circle
        cx={spec.attrs.cx as number}
        cy={spec.attrs.cy as number}
        r={spec.attrs.r as number}
        fill={color}
        stroke={stroke}
        strokeWidth={spec.attrs.strokeWidth as number}
      />
    );
  }

  return (
    <svg
      width={dim}
      height={dim}
      viewBox={`0 0 ${dim} ${dim}`}
      className={className}
      aria-hidden
    >
      {body}
    </svg>
  );
}

export default function ProductVisualPicker({
  color,
  shape,
  size,
  readOnly,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const current = resolved({ color, shape, size });
  const previewSize = previewProductSize(current.size);
  const tooltip = `${current.shape}, ${current.color}, ${current.size}px`;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 224;
    const panelHeight = 280;
    const margin = 8;
    let left = rect.left;
    let top = rect.bottom + margin;

    if (left + panelWidth > window.innerWidth - margin) {
      left = window.innerWidth - panelWidth - margin;
    }
    if (top + panelHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - panelHeight - margin);
    }

    setPanelStyle({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  if (readOnly) {
    return (
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-surface-raised"
        title={tooltip}
      >
        <ShapeSvg
          shape={current.shape}
          color={current.color}
          size={previewSize}
        />
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        className={`flex h-9 w-9 items-center justify-center rounded-lg border bg-surface-raised transition hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
          open ? 'border-accent ring-1 ring-accent/40' : 'border-border'
        }`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        title={tooltip}
        onClick={() => setOpen((value) => !value)}
      >
        <ShapeSvg
          shape={current.shape}
          color={current.color}
          size={previewSize}
        />
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Close appearance picker"
            onClick={() => setOpen(false)}
          />
          <div
            id={panelId}
            role="dialog"
            aria-label="Product appearance"
            className="fixed z-50 w-56 rounded-lg border border-border bg-surface-raised p-3 shadow-xl"
            style={{ top: panelStyle.top, left: panelStyle.left }}
          >
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
            Appearance
          </p>

          <div className="mb-3 flex items-center justify-center rounded-lg border border-border/60 bg-surface py-3">
            <ShapeSvg
              shape={current.shape}
              color={current.color}
              size={Math.min(40, Math.max(16, current.size))}
            />
          </div>

          <p className="mb-1.5 text-[10px] text-text-muted">Shape</p>
          <div className="mb-3 grid grid-cols-5 gap-1">
            {PRODUCT_SHAPES.map((option) => {
              const selected = current.shape === option;
              return (
                <button
                  key={option}
                  type="button"
                  title={option}
                  className={`flex h-8 items-center justify-center rounded-md border transition hover:border-accent ${
                    selected
                      ? 'border-accent bg-accent/15 ring-1 ring-accent/40'
                      : 'border-border/60 bg-surface'
                  }`}
                  onClick={() =>
                    onChange({
                      color: current.color,
                      shape: option,
                      size: current.size,
                    })
                  }
                >
                  <ShapeSvg
                    shape={option}
                    color={current.color}
                    size={18}
                  />
                </button>
              );
            })}
          </div>

          <div className="mb-3 flex items-center justify-between gap-2">
            <label className="text-[10px] text-text-muted">Color</label>
            <input
              type="color"
              className="h-8 w-14 cursor-pointer rounded border border-border bg-transparent p-0.5"
              value={current.color}
              onChange={(e) =>
                onChange({
                  color: e.target.value,
                  shape: current.shape as ProductShape,
                  size: current.size,
                })
              }
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="text-[10px] text-text-muted">Size</label>
              <span className="text-[10px] tabular-nums text-text-subtle">
                {current.size}px
              </span>
            </div>
            <input
              type="range"
              min={MIN_PRODUCT_SIZE}
              max={MAX_PRODUCT_SIZE}
              step={1}
              className="w-full accent-accent"
              value={current.size}
              onChange={(e) =>
                onChange({
                  color: current.color,
                  shape: current.shape as ProductShape,
                  size: Number(e.target.value),
                })
              }
            />
          </div>
          </div>
        </>
      )}
    </div>
  );
}

// Re-export defaults for callers that need them.
export {
  DEFAULT_PRODUCT_COLOR,
  DEFAULT_PRODUCT_SHAPE,
  DEFAULT_PRODUCT_SIZE,
  normalizeShape,
};
